<template>
  <b-card class="calculation-reference-panel">
    <h6 class="mb-0">{{ $t('baseSettlementValue') }} {{ $t('internal') }}</h6>

    <table class="reference-comparison">
      <thead>
        <tr>
          <th scope="col"></th>
          <th scope="col">{{ $t('calculationValuesInEur') }}</th>
          <th scope="col">{{ $t('calculationValuesInEurPerKwp') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in dataRows" :key="row.labelKey">
          <th scope="row">
            {{ $t(row.labelKey)
            }}<template v-if="row.percentage != null">
              ({{ formatReferenceValue(row.percentage / 100, NUMBER_FORMAT.PERCENT) }})</template
            >
          </th>
          <td>{{ formatReferenceValue(row.valueInEur, NUMBER_FORMAT.CURRENCY) }}</td>
          <td>{{ formatReferenceValue(row.valueInEurPerKwp, NUMBER_FORMAT.CURRENCY) }}</td>
        </tr>
        <tr class="reference-comparison-section-start">
          <th scope="row">
            <strong
              >{{ $t('baseSettlementValue') }} {{ $t('internal') }}
              {{ $t('withoutProfitMargin') }}</strong
            >
          </th>
          <td>
            <strong>{{ formatReferenceValue(sumInEur, NUMBER_FORMAT.CURRENCY) }}</strong>
          </td>
          <td>
            <strong>{{ formatReferenceValue(sumInEurPerKwp, NUMBER_FORMAT.CURRENCY) }}</strong>
          </td>
        </tr>
        <tr>
          <th scope="row">
            {{ $t('margin')
            }}<template v-if="additionalProfitMargin != null">
              ({{
                formatReferenceValue(additionalProfitMargin / 100, NUMBER_FORMAT.PERCENT)
              }})</template
            >
          </th>
          <td>{{ formatReferenceValue(additionalProfitMarginInEur, NUMBER_FORMAT.CURRENCY) }}</td>
          <td>
            {{ formatReferenceValue(additionalProfitMarginInEurPerKwp, NUMBER_FORMAT.CURRENCY) }}
          </td>
        </tr>
        <tr class="reference-comparison-section-start">
          <th scope="row">
            <strong>{{ $t('baseSettlementValue') }} {{ $t('internal') }}</strong>
          </th>
          <td>
            <strong>{{
              formatReferenceValue(sumWithAdditionalProfitMarginInEur, NUMBER_FORMAT.CURRENCY)
            }}</strong>
          </td>
          <td>
            <strong>{{
              formatReferenceValue(sumWithAdditionalProfitMarginInEurPerKwp, NUMBER_FORMAT.CURRENCY)
            }}</strong>
          </td>
        </tr>
      </tbody>
    </table>
  </b-card>
</template>

<script>
import { formatNumber, NUMBER_FORMAT } from '@/helpers/numberFormat';

export default {
  name: 'ProjectCalculationBaseReferencePanel',
  props: {
    costCalculationBasis: { type: String, default: null },
    solarPlantOverallPower: { type: Number, default: 0 },
    calculatedExternalCost: { type: Number, default: 0 },
    projectRightsCost: { type: Number, default: 0 },
    effectiveProjectLeaseCost: { type: Number, default: 0 },
    effectiveTotalPlannedMaterialCost: { type: Number, default: 0 },
    effectiveTotalLaborCost: { type: Number, default: 0 },
    effectiveGeneralBusinessCosts: { type: Number, default: 0 },
    effectiveGeneralBusinessCostsPercentage: { type: Number, default: null },
    effectiveRiskAndProfit: { type: Number, default: 0 },
    effectiveProfitMarginPercentage: { type: Number, default: null },
    additionalProfitMargin: { type: Number, default: null },
  },
  data() {
    return {
      NUMBER_FORMAT,
    };
  },
  computed: {
    dataRows() {
      const rows = [
        {
          labelKey: 'effectivePlannedExternalCost',
          valueInEur: this.getUiEffectiveValue(
            'effectivePlannedExternalCost',
            this.calculatedExternalCost
          ),
        },
        {
          labelKey: 'projectRightsCost',
          valueInEur: this.getUiEffectiveValue('projectRightsCost', this.projectRightsCost),
        },
        {
          labelKey: 'effectiveProjectLeaseCost',
          valueInEur: this.getUiEffectiveValue(
            'effectiveProjectLeaseCost',
            this.effectiveProjectLeaseCost
          ),
        },
        {
          labelKey: 'effectiveTotalPlannedMaterialCost',
          valueInEur: this.getUiEffectiveValue(
            'effectiveTotalPlannedMaterialCost',
            this.effectiveTotalPlannedMaterialCost
          ),
        },
        {
          labelKey: 'effectiveTotalLaborCost',
          valueInEur: this.getUiEffectiveValue(
            'effectiveTotalLaborCost',
            this.effectiveTotalLaborCost
          ),
        },
        {
          labelKey: 'projectGeneralBusinessCosts',
          valueInEur: this.effectiveGeneralBusinessCosts ?? 0,
          percentage: this.effectiveGeneralBusinessCostsPercentage,
        },
        {
          labelKey: 'projectRiskAndProfit',
          valueInEur: this.effectiveRiskAndProfit ?? 0,
          percentage: this.effectiveProfitMarginPercentage,
        },
      ];

      return rows.map((row) => ({
        ...row,
        valueInEurPerKwp: this.toEurPerKwp(row.valueInEur),
      }));
    },

    sumInEur() {
      return this.dataRows.reduce((sum, row) => sum + row.valueInEur, 0);
    },

    sumInEurPerKwp() {
      return this.toEurPerKwp(this.sumInEur);
    },

    additionalProfitMarginInEur() {
      const marginPercent = this.additionalProfitMargin ?? 0;
      return (this.sumInEur * marginPercent) / 100;
    },

    additionalProfitMarginInEurPerKwp() {
      return this.toEurPerKwp(this.additionalProfitMarginInEur);
    },

    sumWithAdditionalProfitMarginInEur() {
      return this.sumInEur + this.additionalProfitMarginInEur;
    },

    sumWithAdditionalProfitMarginInEurPerKwp() {
      return this.toEurPerKwp(this.sumWithAdditionalProfitMarginInEur);
    },
  },
  methods: {
    formatNumber,
    /**
     * Mirrors ProjectCalculationService costCalculationBasis zeroing for instant UX.
     * Backend resolver values remain source of truth after save/refetch.
     */
    getUiEffectiveValue(labelKey, rawValue) {
      if (
        labelKey === 'effectivePlannedExternalCost' &&
        this.costCalculationBasis === 'ONLY_INTERNAL_COST'
      ) {
        return 0;
      }
      if (
        (labelKey === 'projectRightsCost' || labelKey === 'effectiveProjectLeaseCost') &&
        this.costCalculationBasis === 'ONLY_EXTERNAL_COST'
      ) {
        return 0;
      }
      if (
        (labelKey === 'effectiveTotalPlannedMaterialCost' ||
          labelKey === 'effectiveTotalLaborCost') &&
        (this.costCalculationBasis === 'ONLY_EXTERNAL_COST' ||
          this.costCalculationBasis === 'EXTERNAL_AND_LEASE_AND_RIGHTS')
      ) {
        return 0;
      }
      return rawValue ?? 0;
    },
    toEurPerKwp(valueInEur) {
      if (this.solarPlantOverallPower === 0) {
        return 0;
      }
      return valueInEur / this.solarPlantOverallPower;
    },
    formatReferenceValue(value, format) {
      if (value == null) {
        return '–';
      }
      return formatNumber(value, format);
    },
  },
};
</script>

<style scoped lang="scss">
.calculation-reference-panel {
  position: sticky;
  top: 1rem;
}

.reference-comparison {
  width: 100%;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  margin-top: 0.75rem;

  th,
  td {
    padding: 0.25rem 0.5rem;
    vertical-align: baseline;
  }

  th {
    font-weight: normal;
  }

  thead th {
    color: #6c757d;
  }

  th:not(:first-child),
  td {
    text-align: right;
    white-space: nowrap;
  }

  tbody th {
    // color: #6c757d;
    padding-left: 0;
    text-align: left;
    white-space: normal;
  }

  .reference-comparison-section-start th,
  .reference-comparison-section-start td {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 0.5rem;
  }
}
</style>
